/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, ReactElement } from "react";
import { Badge } from "@chakra-ui/react";
import { MdOutlineDoNotDisturbAlt } from "react-icons/md";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { Tooltip, IconButton } from "@mui/material";
import { MdOutlineContentCopy, MdInfoOutline } from "react-icons/md";
import { TbRefresh } from "react-icons/tb";
import UpdateApiStatusConfirmModal from "./UpdateApiStatusConfirmModal";
import ResetAPIKeyConfirmModal from "./ResetAPIKeyConfirmModal";
import ResetAPISecretConfirmModal from "./ResetAPISecretConfirmModal";
import ResetAPISecretSuccessModal from "./ResetAPISecretSuccessModal";

const AccessControlTab: React.FC<{
  data: any;
  mode: "merchant" | "subaccount";
  is_active: string;
  auth1: string;
  auth2: string;
  setSwalProps: any;
  isProcessing: boolean;
  fetchDetail: any;
  isOtherTabUpdating: boolean;
  setIsUpdating: any;
  isImpersonating?: boolean;
}> = ({
  data,
  mode,
  is_active,
  auth1,
  auth2,
  setSwalProps,
  isProcessing,
  fetchDetail,
  isOtherTabUpdating,
  setIsUpdating,
  isImpersonating,
}) => {
  const [details, setDetails] = useState<any>(null);
  const [activationStatus, setActivationStatus] = useState<string>("");
  const [apiKey, setApiKey] = useState<string>("");
  const [apiSecret, setApiSecret] = useState<string>("");
  const [statusToUpdate, setStatusToUpdate] = useState<string>("");
  const [
    isOpenUpdateApiStatusConfirmModal,
    setIsOpenUpdateApiStatusConfirmModal,
  ] = useState<boolean>(false);
  const [isOpenResetAPIKeyConfirmModal, setIsOpenResetAPIKeyConfirmModal] =
    useState<boolean>(false);
  const [
    isOpenResetAPISecretConfirmModal,
    setIsOpenResetAPISecretConfirmModal,
  ] = useState<boolean>(false);

  const [resetAPISecretInfo, setResetAPISecretInfo] = useState<any>(null);
  const [
    isOpenResetAPISecretSuccessModal,
    setIsOpenResetAPISecretSuccessModal,
  ] = useState<boolean>(false);

  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  interface MerchantStatusItems {
    status: string;
    value: string;
    display: ReactElement;
  }

  const statusClasses = "status";

  const statusList: MerchantStatusItems[] = [
    {
      status: "yes",
      value: "yes",
      display: (
        <Badge className={`${statusClasses} status-active`}>Active</Badge>
      ),
    },
    {
      status: "no",
      value: "no",
      display: (
        <Badge className={`${statusClasses} status-inactive`}>Inactive</Badge>
      ),
    },
  ];

  const statusDisplay = (status: string) => {
    const statusItem = statusList.find((item) => item.status === status);
    return statusItem ? statusItem.display : null;
  };

  useEffect(() => {
    if (data !== null) {
      setDetails(data);
    }
  }, [data]);

  useEffect(() => {
    if (is_active !== undefined) {
      setActivationStatus(is_active);
      setApiKey(auth1);
      setApiSecret(auth2);
    }
  }, [is_active, auth1, auth2]);

  const handleClickCopy = async (payoutId: string) => {
    try {
      await navigator.clipboard.writeText(payoutId);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const showUpdateApiStatusConfirmModal = (statusToUpdate: string) => {
    setStatusToUpdate(statusToUpdate);
    setIsOpenUpdateApiStatusConfirmModal(true);
  };

  const closeUpdateApiStatusConfirmModal = () => {
    setStatusToUpdate("");
    setIsOpenUpdateApiStatusConfirmModal(false);
  };

  const showResetAPIKeyConfirmModal = () => {
    setIsOpenResetAPIKeyConfirmModal(true);
  };

  const closeResetAPIKeyConfirmModal = () => {
    setIsOpenResetAPIKeyConfirmModal(false);
  };

  const showResetAPISecretConfirmModal = () => {
    setIsOpenResetAPISecretConfirmModal(true);
  };

  const closeResetAPISecretConfirmModal = () => {
    setIsOpenResetAPISecretConfirmModal(false);
  };

  const showResetAPISecretSuccessModal = () => {
    setIsOpenResetAPISecretSuccessModal(true);
  };

  const closeResetAPISecretSuccessModal = () => {
    setResetAPISecretInfo(null);
    setIsOpenResetAPISecretSuccessModal(false);
  };

  return (
    <>
      {isOpenUpdateApiStatusConfirmModal && details && (
        <UpdateApiStatusConfirmModal
          details={details}
          mode={mode}
          statusToUpdate={statusToUpdate}
          isOpen={isOpenUpdateApiStatusConfirmModal}
          onClose={closeUpdateApiStatusConfirmModal}
          triggerReload={fetchDetail}
          setSwalProps={setSwalProps}
        />
      )}
      {isOpenResetAPIKeyConfirmModal && details && (
        <ResetAPIKeyConfirmModal
          details={details}
          mode={mode}
          isOpen={isOpenResetAPIKeyConfirmModal}
          onClose={closeResetAPIKeyConfirmModal}
          setSwalProps={setSwalProps}
          triggerReload={fetchDetail}
          activationStatus={activationStatus}
        />
      )}
      {isOpenResetAPISecretConfirmModal && details && (
        <ResetAPISecretConfirmModal
          details={details}
          mode={mode}
          isOpen={isOpenResetAPISecretConfirmModal}
          onClose={closeResetAPISecretConfirmModal}
          setSwalProps={setSwalProps}
          showResetAPISecretSuccessModal={showResetAPISecretSuccessModal}
          setResetAPISecretInfo={setResetAPISecretInfo}
          activationStatus={activationStatus}
          apiKey={apiKey}
        />
      )}
      {isOpenResetAPISecretSuccessModal && resetAPISecretInfo && (
        <ResetAPISecretSuccessModal
          details={resetAPISecretInfo}
          mode={mode}
          isOpen={isOpenResetAPISecretSuccessModal}
          onClose={closeResetAPISecretSuccessModal}
          triggerReload={fetchDetail}
        />
      )}

      <div className="flex px-4 py-8">
        <div className="w-full">
          {details ? (
            <>
              {userRole === "superadmin" && (
                <div className="mb-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                  <div className="mb-2 text-sm font-semibold text-navy-700 dark:text-white">
                    {mode === "merchant" ? "Merchant" : "Subaccount"} API Status
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                      <div className="text-sm text-navy-700 dark:text-white">
                        Current Status:
                      </div>
                      <div className="font-semibold">
                        {statusDisplay(details.is_active)}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {details.is_active === "yes" ? (
                        <Tooltip
                          title={`Deactivate ${
                            mode === "merchant" ? "Merchant" : "Subaccount"
                          } API Status`}
                          arrow
                          placement="top"
                        >
                          <IconButton
                            disabled={isProcessing || isOtherTabUpdating}
                            id="button-deactivate"
                            onClick={() =>
                              showUpdateApiStatusConfirmModal("no")
                            }
                          >
                            <MdOutlineDoNotDisturbAlt className="h-6 w-6 text-red-500" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={`Activate ${
                            mode === "merchant" ? "Merchant" : "Subaccount"
                          } API Status`}
                          arrow
                          placement="top"
                        >
                          <IconButton
                            disabled={isProcessing || isOtherTabUpdating}
                            id="button-activate"
                            onClick={() =>
                              showUpdateApiStatusConfirmModal("yes")
                            }
                          >
                            <AiOutlineCheckCircle className="h-6 w-6 text-primary-500" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <div className="mb-2 text-sm font-semibold text-navy-700 dark:text-white">
                  {mode === "merchant" ? "Merchant" : "Subaccount"} API Key
                </div>

                <div className="mb-6">
                  <div className="flex w-full md:w-1/2">
                    <input
                      type="text"
                      value={apiKey}
                      onKeyDown={(e) => e.preventDefault()}
                      className="caret-transparent w-full cursor-default rounded-l-md border-y border-l border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-700 shadow-sm outline-none"
                    />
                    <div className="flex items-center rounded-r-md border border-gray-300 bg-gray-100 px-1">
                      <Tooltip title="Copy API Key" arrow placement="top">
                        <IconButton
                          id="button-copy-auth1"
                          size="small"
                          onClick={() => handleClickCopy(apiKey)}
                        >
                          <MdOutlineContentCopy className="h-5 w-5 text-navy-800" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset API Key" arrow placement="top">
                        <IconButton
                          disabled={isProcessing || isOtherTabUpdating}
                          id="button-reset-api-key"
                          size="small"
                          onClick={showResetAPIKeyConfirmModal}
                        >
                          <TbRefresh className="h-5 w-5 text-navy-800" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <div className="mb-2 text-sm font-semibold text-navy-700 dark:text-white">
                  {mode === "merchant" ? "Merchant" : "Subaccount"} API Secret
                  Key
                </div>
                <div className="mb-6">
                  <div className="flex w-full md:w-1/2">
                    <input
                      type="text"
                      value={apiSecret}
                      onKeyDown={(e) => e.preventDefault()}
                      className="caret-transparent w-full cursor-default rounded-l-md border-y border-l border-gray-300 bg-gray-50 px-4 py-2.5 text-sm text-gray-700 shadow-sm outline-none"
                    />
                    <div className="flex items-center rounded-r-md border border-gray-300 bg-gray-100 px-1">
                      <Tooltip
                        title="You will only see the unmask API Secret once, immediately after it's generated."
                        arrow
                        placement="top"
                      >
                        <IconButton id="button-info-auth2" size="small">
                          <MdInfoOutline className="h-5 w-5 text-navy-800" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset API Secret" arrow placement="top">
                        <IconButton
                          disabled={isProcessing || isOtherTabUpdating}
                          id="button-reset-api-secret"
                          size="small"
                          onClick={showResetAPISecretConfirmModal}
                        >
                          <TbRefresh className="h-5 w-5 text-navy-800" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="h-5 w-full max-w-[240px] animate-pulse rounded-md bg-gray-200"
                  style={{ animationDuration: "0.6s" }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AccessControlTab;
